/**
 * "New version" — saves the plan as it stands as a new version, with an optional label, and
 * makes it the live one. Works in every status but Locked; edits are a draft against the
 * live version until this is pressed, and the saved version stays on the Versions tab.
 *
 * Shown to anyone who can edit the plan; disabled, with the reason, when there is nothing
 * new to save.
 */

import { CopyPlus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCommitPlanVersion } from "@/hooks/use-plan-governance";
import { COMMIT_LABEL_MAX_LENGTH } from "@/lib/plan-version";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export function CommitVersionButton({
  plan,
  variant = "default",
}: {
  plan: ResourcePlanDetailView;
  variant?: "default" | "outline";
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const commit = useCommitPlanVersion(plan.id);

  if (!plan.canEdit) return null;
  const canCommit = plan.canCommitVersion === true;

  function close() {
    setOpen(false);
    setLabel("");
  }

  return (
    <>
      <Button
        size="sm"
        variant={variant}
        onClick={() => setOpen(true)}
        disabled={!canCommit}
        title={
          canCommit
            ? "Save the plan as it stands now as a new version."
            : "No changes since the live version — there is nothing to save."
        }
      >
        <CopyPlus className="h-3.5 w-3.5" />
        New version
      </Button>
      <Dialog open={open} onOpenChange={(next) => !next && close()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save version {plan.versionNumber ?? ""}</DialogTitle>
            <DialogDescription>
              Saves "{plan.name}" as it stands now. You keep editing; this version stays on the
              Versions tab exactly as saved, and you can restore it any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="plan-commit-label">Label (optional)</Label>
            <Input
              id="plan-commit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={COMMIT_LABEL_MAX_LENGTH}
              placeholder="e.g. Pre-kickoff"
            />
            <p className="text-xs text-muted-foreground">
              Shown next to the version in the Versions tab.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={commit.isPending}>
              Cancel
            </Button>
            <Button
              disabled={commit.isPending}
              onClick={() =>
                commit.mutate(
                  { label: label.trim() === "" ? null : label.trim() },
                  { onSuccess: close },
                )
              }
            >
              {commit.isPending ? "Saving…" : "Save version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
